import clsx from 'clsx'
import {
  Button,
  Dropdown,
  DropdownItem,
  Icon,
  Popover,
  Typography,
} from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import styles from 'components/Header/subcomponents/UserMenu/user-menu.module.scss'
import UserSwitcher from 'components/Header/subcomponents/UserSwitcher'
import useSwitchedUser from 'utils/hooks/useSwitchedUser'
import { CURRENCIES } from 'constants/app-config'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { notificationShow } from 'modules/Notifications/actions'
import { LOGOUT } from 'modules/Auth/types'

const UserMenu = () => {
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const writeToClipboard = useWriteToClipboard()
  const pathGen = useGeneratePathI18n()
  const breakpoints = useBreakpoint()

  const {
    id,
    email,
    is_email_confirmed,
    is_switch_user_available,
    is_agency_account_available,
    is_promised_payment_available,
  } = useSelector(({ app }) => app.user)

  const dispatch = useDispatch()
  const { t } = useTranslation(['common'])
  const { switchedUser, dropSwitchedUser } = useSwitchedUser()

  const popoverState = usePopupState({
    variant: 'popover',
    id: 'userMenuDropdown',
  })

  const toClipboard = useCallback((value) => {
    writeToClipboard(value)

    dispatch(
      notificationShow({
        type: 'success',
        message: 'common::notifications.success_copy_notification',
      }),
    )
  }, [])

  let timer = null

  const onMouseEnter = () => clearTimeout(timer)

  const onMouseLeave = () => {
    timer = setTimeout(() => {
      popoverState.close()
      clearTimeout(timer)
    }, 30000)
  }

  const handleClick = () => popoverState.close()

  const isEmailConfirmed = useMemo(
    () => (typeof is_email_confirmed === 'boolean' ? is_email_confirmed : null),
    [is_email_confirmed],
  )

  return (
    <div className={styles['user-menu']}>
      <Button
        {...bindTrigger(popoverState)}
        className={clsx(styles['user-menu__avatar'], {
          [styles['user-menu__avatar_switched']]: switchedUser,
          [styles['user-menu__avatar_switch-available']]:
            is_switch_user_available,
        })}
        bgColor={'Background-Quaternary'}
        bgColorHover={'Background-Fifth'}
        bgColorActive={'Background-Fifth'}
        variant={'default'}
        color={'Interface-GU-Primary'}
        size={breakpoints.fb ? 'm' : 's'}
        startIcon={<Icon name={switchedUser ? 'users' : 'user'} size={'m'} />}
        noLabel
        additionalIcon={
          isEmailConfirmed === false ? (
            <Icon
              className={styles['user-menu__avatar-additional-icon']}
              name={'status warning2'}
              size={'s'}
              color={'Accent-Error-Primary'}
            />
          ) : null
        }
      />

      <Popover
        className={styles['popover']}
        {...bindPopover(popoverState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 8,
          horizontal: 'right',
        }}
        breakpoints={{
          0: {
            variant: 'bottomSheet',
            hasBackdrop: true,
          },
          768: {
            variant: 'popover',
            hasBackdrop: false,
          },
        }}
      >
        <Dropdown
          className={styles['dropdown']}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
        >
          {/*//user profile*/}
          <div className={styles['user-menu__user-profile']}>
            {switchedUser && (
              <div className={styles['user-menu__logged-with-previous-admin']}>
                {t('common::user_menu.logged_with_user')}
              </div>
            )}
            <div
              className={clsx(
                styles['user-menu__avatar'],
                styles['user-menu__user-profile-avatar'],
              )}
            >
              <Icon
                className="icon"
                name={'user'}
                size={'m'}
                color={'Interface-GU-Primary'}
              />
            </div>

            <Typography
              color={'Label-Fifth'}
              variant={'Body-20'}
              component={'span'}
              className={styles['user-menu__user-profile-email']}
            >
              {email}
            </Typography>

            <Typography
              variant={'Body-15'}
              component={'span'}
              color={'Label-Secondary'}
              onClick={() => toClipboard(id)}
              className={styles['user-menu__user-profile-id-copy']}
            >
              <span className={styles['user-menu__user-profile-id']}>
                ID: {id}
              </span>
              <Icon name={'copy'} color={'inherit'} width={20} />
            </Typography>

            {breakpoints.sm && !switchedUser && (
              <Button
                component={Link}
                className={clsx(
                  'margin-top-16',
                  styles['user-menu__action-button'],
                )}
                variant={'primary'}
                size={'s'}
                startIcon={<Icon name={'plus'} />}
                to={pathGen(ROUTES.BILLING_TOP_UP)}
              >
                {t('common::user_menu.add_funds')}
              </Button>
            )}

            {/*//user profile if user switched*/}
            {switchedUser && (
              <Button
                className={styles['user-menu__action-button']}
                title={t('common::user_menu.logout')}
                size={'s'}
                color={'Label-Fifth'}
                borderColor={'Interface-Quaternary'}
                borderColorHover={'Interface-Seventh'}
                type={'button'}
                variant={'secondary'}
                onClick={dropSwitchedUser}
                startIcon={<Icon name="logout" />}
              >
                {t('common::user_menu.logout_from_account')}
              </Button>
            )}

            {is_switch_user_available && !is_agency_account_available && (
              <UserSwitcher
                email={email}
                id={id}
                className={clsx(
                  styles['user-menu__action-button'],
                  styles['user-menu__user-switcher'],
                )}
              />
            )}
          </div>

          <div className={styles['dropdown__divider']} />
          <DropdownItem size={'m'} noHover>
            <span>{t('common::user_menu.finance')}</span>
          </DropdownItem>

          <DropdownItem
            size={'m'}
            component={Link}
            onClick={handleClick}
            to={pathGen(ROUTES.FINANCE_WALLETS)}
            startIcon={
              <Icon name={'card'} size={'m'} color={'Interface-GU-Primary'} />
            }
          >
            <Typography component={'span'} variant={'Body-15'}>
              {t('common::user_menu.balance')}
            </Typography>
          </DropdownItem>

          <DropdownItem
            size={'m'}
            component={Link}
            onClick={handleClick}
            to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILES)}
            startIcon={
              <Icon name={'user'} size={'m'} color={'Interface-GU-Primary'} />
            }
          >
            <Typography component={'span'} variant={'Body-15'}>
              {t('common::user_menu.payment-profiles')}
            </Typography>
          </DropdownItem>

          {is_promised_payment_available && (
            <DropdownItem
              size={'m'}
              component={Link}
              onClick={handleClick}
              to={pathGen(ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST)}
              startIcon={
                <Icon
                  name={'time-clock'}
                  color={'Interface-GU-Primary'}
                  size={'m'}
                />
              }
            >
              <Typography component={'span'} variant={'Body-15'}>
                {t('common::header.credit_deposit')}
              </Typography>
            </DropdownItem>
          )}

          <span className={styles['dropdown__divider']} />

          <DropdownItem
            size={'m'}
            component={Link}
            onClick={handleClick}
            to={pathGen(ROUTES.USER_SETTINGS)}
            startIcon={
              <Icon
                name={'settings'}
                size={'m'}
                color={'Interface-GU-Primary'}
              />
            }
            endIcon={
              isEmailConfirmed === false ? (
                <Icon
                  className={styles['user-menu__avatar-additional-icon']}
                  name={'status warning2'}
                  size={'l'}
                  color={'Accent-Error-Primary'}
                />
              ) : null
            }
          >
            <Typography component={'span'} variant={'Body-15'}>
              {t('common::user_menu.settings')}
            </Typography>
          </DropdownItem>

          <DropdownItem
            size={'m'}
            component={'a'}
            onClick={handleClick}
            target={'_blank'}
            href={pathGen(ROUTES.FAQ)}
            startIcon={
              <Icon
                name={'question'}
                size={'m'}
                color={'Interface-GU-Primary'}
              />
            }
          >
            <Typography component={'span'} variant={'Body-15'}>
              {t('common::user_menu.help')}
            </Typography>
          </DropdownItem>

          <DropdownItem
            size={'m'}
            component={'a'}
            href={pathGen(ROUTES.AUTH_LOGOUT)}
            onClick={(e) => {
              e.preventDefault()
              handleClick()
              dispatch({ type: LOGOUT })
            }}
            startIcon={
              <Icon size="m" name="logout" color={'Interface-GU-Primary'} />
            }
          >
            <Typography component={'span'} variant={'Body-15'}>
              {t('common::user_menu.logout')}
            </Typography>
          </DropdownItem>
        </Dropdown>
      </Popover>
    </div>
  )
}

UserMenu.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  balance: PropTypes.shape({
    currency: PropTypes.oneOf(CURRENCIES),
  }),
}

export default UserMenu
